import { Button } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { ReactComponent as MagicIcon } from 'src/assets/icons/magic-icon.svg';
import { FiCheck, FiX } from 'react-icons/fi';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { useQuery } from 'react-query';
import queryKeys from 'src/constants/queryKeys';
import { chatgenieAPI } from 'src/config/api';
import { ReactComponent as Spinner } from 'src/assets/icons/spinner.svg';
import {
  ModalStyled,
  Wrapper,
  TemplateCard,
  TemplateContent,
  TemplateSidebar,
  TemplateActions,
  TemplateCategory
} from './style';

const getCategoryEmojiAndName = category => {
  if (!category) {
    return ['', ''];
  }
  const emojiRegex = /[\p{Emoji}\p{Extended_Pictographic}]/gu;
  if (!emojiRegex.test(category)) {
    return ['💡', category];
  }
  const emoji = category.match(emojiRegex).join('');
  const name = category.replace(emoji, '').trim();
  return [emoji, name];
};

export default function IntentTemplatesModal({
  isOpen,
  onClose,
  onSubmit,
  isAddingAllTemplates,
  onAddAllTemplates
}) {
  const { data } = useQuery([queryKeys.GET_INTENT_TEMPLATES], chatgenieAPI.getAgentIntentTemplates);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [activeCategoryEmoji, activeCategoryName] = getCategoryEmojiAndName(activeCategory);
  const theme = useTheme();

  const categories = useMemo(() => {
    if (data?.length) {
      const categorySet = new Set(data.map(item => item.category_name));
      return Array.from(categorySet);
    }
    return [];
  }, [data]);

  const templates = useMemo(() => {
    if (!data?.length) {
      return [];
    }
    return data.filter(template => template.category_name === activeCategory);
  }, [activeCategory]);

  const handleTemplateClick = template => {
    setActiveTemplate(old => (old?.id === template.id ? null : template));
  };

  const handleCategoryClick = category => {
    setActiveCategory(category);
    setActiveTemplate(null);
  };

  useEffect(() => {
    if (!activeCategory && categories.length) {
      setActiveCategory(categories[0]);
    }
  }, [categories]);

  return (
    <ModalStyled centered isOpen={isOpen} toggle={onClose}>
      {!data?.length ? (
        <Wrapper>
          <Spinner width="24" />
        </Wrapper>
      ) : (
        <Wrapper>
          <button type="button" onClick={onClose}>
            <FiX />
          </button>
          <TemplateSidebar>
            <h6>Templates</h6>
            <ul>
              {categories.map(category => {
                const [emoji, name] = getCategoryEmojiAndName(category);
                const isActive = activeCategory === category;
                return (
                  <TemplateCategory
                    key={category}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    onClick={() => {
                      handleCategoryClick(category);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCategoryClick(category);
                      }
                    }}
                    isActive={isActive}
                  >
                    <span>{emoji}</span>
                    {name}
                  </TemplateCategory>
                );
              })}
            </ul>
          </TemplateSidebar>
          <TemplateContent>
            <h2>
              <span role="img" aria-label="user">
                {activeCategoryEmoji}
              </span>
              {activeCategoryName}
            </h2>
            <main>
              {templates.map(template => {
                const isActive = activeTemplate?.id === template.id;
                return (
                  <TemplateCard
                    key={template.name}
                    isActive={isActive}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleTemplateClick(template);
                      }
                    }}
                    onClick={() => {
                      handleTemplateClick(template);
                    }}
                  >
                    <span>
                      <MagicIcon />
                      {isActive && <FiCheck color={theme.colors.brand.light.base} size={16} />}
                    </span>
                    <p>{template.name}</p>
                  </TemplateCard>
                );
              })}
            </main>
            <TemplateActions>
              {onAddAllTemplates && (
                <Button loading={isAddingAllTemplates} onClick={onAddAllTemplates}>
                  Add all templates
                </Button>
              )}
              <Button
                disabled={!activeTemplate}
                type="primary"
                onClick={() => onSubmit(activeTemplate)}
              >
                Start from template
              </Button>
            </TemplateActions>
          </TemplateContent>
        </Wrapper>
      )}
    </ModalStyled>
  );
}

IntentTemplatesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isAddingAllTemplates: PropTypes.bool,
  onAddAllTemplates: PropTypes.func
};

IntentTemplatesModal.defaultProps = {
  onAddAllTemplates: null,
  isAddingAllTemplates: false
};
